
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from 'react-router-dom';
import { GraduationCap, TrendingUp, Zap, Layers, ChartBar, Shield } from "lucide-react";
import Testimonials from '../components/testimonials';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: "Scenario-Based Learning",
      description: "Navigate real operational challenges with realistic constraints and consequences.",
      icon: Shield,
      bullets: [
        "Critical incident simulations",
        "Real-time decision branches",
        "Industry-accurate workflows"
      ]
    },
    {
      title: "Industry-Modeled Market Dynamics",
      description: "Train with real-world volatility and unpredictable cryptocurrency economics.",
      icon: ChartBar,
      bullets: [
        "Bitcoin price fluctuation models",
        "Network hash rate adjustments",
        "Dynamic energy pricing scenarios"
      ]
    },
    {
      title: "Full-Stack Mining Operations",
      description: "Build and optimize mining infrastructure from one rig to full facilities.",
      icon: Layers,
      bullets: [
        "ASIC selection and configuration",
        "Cooling and power management",
        "Maintenance scheduling"
      ]
    },
    {
      title: "Tiered Simulation Modes",
      description: "Match learning intensity with experience level for optimal progression.",
      icon: Zap,
      bullets: [
        "Guided beginner playthroughs",
        "Advanced operational scenarios",
        "Customizable parameters"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-20">
          {/* Left Column */}
          <div className="text-left space-y-6">
            <div className="space-y-2">
              <h1 className="text-6xl font-bold">
                <span className="block text-bitcoin-accent">Bitcoin Mining</span>
                <span className="block text-white">Career Simulator</span>
              </h1>
              <p className="text-xl text-white max-w-xl">
                Dive into the dynamic world of Bitcoin mining through an immersive, hyper-realistic career simulation. 
                Navigate complex technological challenges, make critical infrastructure decisions, 
                and build your expertise from a novice miner to an industry leader. 
                Experience the entire ecosystem – from understanding blockchain technology 
                to managing cutting-edge mining operations across global landscapes.
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <h2 className="sr-only">Get Started Options</h2>
            <Card className="hover:shadow-lg transition-shadow bg-web3-blue/10 border border-web3-blue/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-web3-green">
                  <GraduationCap className="h-6 w-6" />
                  Explore Career Paths
                </CardTitle>
                <CardDescription className="text-white/80">
                  Discover different roles and progression paths in Bitcoin mining
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full"
                  onClick={() => navigate('/careers')}
                >
                  View Career Paths
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow bg-web3-blue/10 border border-web3-blue/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-web3-green">
                  <TrendingUp className="h-6 w-6" />
                  Start Your Journey
                </CardTitle>
                <CardDescription className="text-white/80">
                  Begin your career in the Bitcoin mining industry
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full"
                  onClick={() => navigate('/careers')}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Key Features Section */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12 text-web3-green">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-web3-blue/10 border border-web3-blue/20">
                <CardHeader>
                  <feature.icon className="h-8 w-8 text-web3-green mb-2" />
                  <CardTitle className="text-lg text-white">{feature.title}</CardTitle>
                  <CardDescription className="text-white/80">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside text-sm text-white/80">
                    {feature.bullets.map((bullet, idx) => (
                      <li key={idx} className="mb-1">{bullet}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12 text-web3-green">What Our Users Say</h2>
          <Testimonials />
        </div>
      </div>
    </div>
  );
};

export default Landing;
